import React, {useEffect, useState} from "react";
import {
    IonButton,
    IonCard, IonCardContent,
    IonCardHeader, IonCardTitle,
    IonContent, IonIcon, IonInput, IonItem, IonLabel,
    IonLoading,
    IonPage, IonPopover, useIonAlert, useIonModal
} from "@ionic/react";
import {
    addOutline,
    addSharp,
    attachOutline,
    attachSharp,
    cloudUploadOutline,
    cloudUploadSharp,
    copyOutline,
    copySharp,
    documentOutline,
    documentSharp,
    eyeOffOutline,
    eyeOffSharp,
    eyeOutline,
    eyeSharp, paperPlaneOutline, paperPlaneSharp,
    pencilOutline,
    pencilSharp
} from "ionicons/icons";
import './AcaViewContact.css';
import {ContactDependentType, ContactNote, ContactType, RecentOpportunityChanges} from "./types";
import {useMutation} from "@apollo/client";
import {
    ACA_CREATE_NOTE,
    ACA_GET_CONTACT,
    ACA_GET_HISTORY,
    ACA_GET_IMAGE_URL,
    ACA_GET_CONTACT_UNMASKED,
    ACA_UPLOAD_IMAGE, ACA_UPDATE_CONTACT, ACA_ISSUE_IMAGE_REPLACEMENT, ACA_CREATE_OPPORTUNITY
} from "../../schemas/aca";
import {useHistory, useRouteMatch} from "react-router";
import {formatDatetime, formatPhone, formatStatus} from "../../utils";
import {AcaHeader} from "./AcaHeader";
import {OverlayEventDetail} from "@ionic/react/dist/types/components/react-component-lib/interfaces";
import ModalNote from "./modals/ModalNote";
import {AcaGetHistoryResponseHistory} from "@backend/types/aca";
import useAuth from "../../hooks/useAuth";
import ModalImage from "./modals/ModalImage";
import AcaUploadFile, {AcaSingleUploadI} from "./components/AcaUploadFile";
import ModalImageReplacementRequest from "./modals/ModalImageReplacementRequest";
import MissingItem from "../../components/MissingItem";
type Attachment = {
    filename: string;
    url: string;
    label?: string;
}

const AcaViewContact: React.FC<any> = () => {

    const {claims} = useAuth();
    const history = useHistory();
    const DEFAULT_LOADING_MESSAGE: string = 'Please wait...';
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string>(DEFAULT_LOADING_MESSAGE);
    const [showPopover, setShowPopover] = React.useState<boolean>(false);
    const [showNotes, setShowNotes] = React.useState<boolean>(true);
    const [contact, setContact] = React.useState<ContactType | undefined>(undefined);
    const [contactNotes, setContactNotes] = React.useState<ContactNote[] | undefined>(undefined);
    const [opportunities, setOpportunities] = React.useState<RecentOpportunityChanges[] | undefined>(undefined);
    const [dependents, setDependents] = React.useState<ContactDependentType[] | undefined>(undefined);
    const [historyEntries, setHistoryEntries] = React.useState<AcaGetHistoryResponseHistory[] | undefined>(undefined);
    const [imageUrl, setImageUrl] = React.useState<string | undefined>(undefined);
    const [masked, setMasked] = React.useState<boolean>(true);

    const [presentImage, dismissImage] = useIonModal(ModalImage, {
        imageUrl: imageUrl,
        dismiss: (data: string, role: string) => dismissImage(data, role)
    });
    const [presentImageReplacement, dismissImageReplacement] = useIonModal(ModalImageReplacementRequest, {
        contact: contact,
        replacementType: 'PHOTO',
        dismiss: (data: string, role: string) => dismissImageReplacement(data, role)
    });
    const [presentAddNote, dismissAddNote] = useIonModal(ModalNote, {dismiss: (data: string, role: string) => dismissAddNote(data, role)});

    const [acaGetContactMutation] = useMutation(ACA_GET_CONTACT);
    const [acaUpdateContactMutation] = useMutation(ACA_UPDATE_CONTACT);
    const [acaGetContactUnmaskedMutation] = useMutation(ACA_GET_CONTACT_UNMASKED);
    const [addNoteMutation] = useMutation(ACA_CREATE_NOTE);
    const [getHistoryMutation] = useMutation(ACA_GET_HISTORY);
    const [getImageUrlMutation] = useMutation(ACA_GET_IMAGE_URL);
    const [uploadImageMutation] = useMutation(ACA_UPLOAD_IMAGE);
    const [issueImageReplacementMutation] = useMutation(ACA_ISSUE_IMAGE_REPLACEMENT);
    const [createOpportunityMutation] = useMutation(ACA_CREATE_OPPORTUNITY);
    const [presentAlert, dismissAlert] = useIonAlert();

    const match:any = useRouteMatch();
    const [tier1, setTier1] = React.useState<boolean>(false);
    const [tier2, setTier2] = React.useState<boolean>(false);
    const [manager, setManager] = React.useState<boolean>(false);


    // replacing images
    const [changingPhoto1, setChangingPhoto1] = useState<boolean>(false);
    const [photo1Attachments, setPhoto1Attachments] = useState<Attachment[]>([]);
    const [changingPhoto2, setChangingPhoto2] = useState<boolean>(false);
    const [photo2Attachments, setPhoto2Attachments] = useState<Attachment[]>([]);

    const addPhoto1Attachment = async ({ filename, url }: AcaSingleUploadI) => {
        const fileParts = filename.split('.');
        const fileType = fileParts[fileParts.length-1];
        if (fileType.toUpperCase() !== 'PNG' && fileType.toUpperCase() !== 'JPEG' && fileType.toUpperCase() !== 'JPG') {
            return alert('Only able to upload PNG or JPG files');
        }
        setIsLoading(true);

        let newFileType : 'JPG' | 'PNG' = 'JPG';
        if (fileType.toUpperCase() === 'PNG') {
            newFileType = 'PNG';
        }

        if (contact) {
            setPhoto1Attachments([{filename, url, label: ""}, ...photo1Attachments])
            const uploadResult = await uploadImageMutation({
                variables: {
                    imageUrl: url,
                    imageName: filename,
                    imageFileType: newFileType
                }
            });
            const updateContactResult = await acaUpdateContactMutation({
                variables: {
                    data: {
                        contactId: contact.contactId,
                        photo1Id: Number(uploadResult.data.acaUploadImage.imageId)
                    }
                }
            });
            setChangingPhoto1(false);
            await loadData(contact.contactId, true);
        }
    }
    const addPhoto2Attachment = async ({ filename, url }: AcaSingleUploadI) => {
        if (contact) {
            const fileParts = filename.split('.');
            const fileType = fileParts[fileParts.length-1];
            if (fileType.toUpperCase() !== 'PNG' && fileType.toUpperCase() !== 'JPEG' && fileType.toUpperCase() !== 'JPG') {
                return alert('Only able to upload PNG or JPG files');
            }

            setIsLoading(true);

            let newFileType : 'JPG' | 'PNG' = 'JPG';
            if (fileType.toUpperCase() === 'PNG') {
                newFileType = 'PNG';
            }
            setPhoto2Attachments([{filename, url, label: ""}, ...photo2Attachments])
            const uploadResult = await uploadImageMutation({
                variables: {
                    imageUrl: url,
                    imageName: filename,
                    imageFileType: newFileType
                }
            });
            const updateContactResult = await acaUpdateContactMutation({
                variables: {
                    data: {
                        contactId: contact.contactId,
                        photo2Id: Number(uploadResult.data.acaUploadImage.imageId)
                    }
                }
            });
            setChangingPhoto2(false);

            await loadData(contact.contactId, true);
        }
    }
    ////
    useEffect(() => {
        // check for roles

        if (!claims.rules.includes('AcaView')) {
            history.push('/');
            return;
        }
        if (claims.rules.includes('AcaEditTier2')) {
            setTier2(true);
        }
        if (claims.rules.includes('AcaEditTier1')) {
            setTier1(true);
        }
        if (claims.rules.includes('AcaManager')) {
            setManager(true);
        }
        if (match.params.id && !isNaN(match.params.id)) {
            loadData(Number(match.params.id), true);
        }
    }, []);
    const expandImage = async (imageId: number | null) => {
        if (imageId) {
            const imageLookup = await getImageUrlMutation({variables: {imageId}});
            if (!imageLookup.data.acaGetImageUrl.success) {
                return alert('Unable to lookup image!');
            }
            setImageUrl(imageLookup.data.acaGetImageUrl.url);
            await presentImage();
        }

    };


    const loadData = async (id: number | null | undefined, ismasked?: boolean) => {
        if (!id) {
            return;
        }
        if (ismasked === undefined || ismasked === null) {
            ismasked = masked;
        }
        setIsLoading(true);
        try {
            let contactResult;
            if (!ismasked && (tier2 || manager)) {
                contactResult = await acaGetContactUnmaskedMutation({variables: {contactId: id}});
                if (contactResult.data?.acaGetContactUnmasked.success) {
                    setContact(contactResult.data.acaGetContactUnmasked.contact);
                    setContactNotes(contactResult.data.acaGetContactUnmasked.notes);
                    setOpportunities(contactResult.data.acaGetContactUnmasked.opportunities);
                    setDependents(contactResult.data.acaGetContactUnmasked.dependents);
                }
            } else {
                contactResult = await acaGetContactMutation({variables: {contactId: id}});
                if (contactResult.data?.acaGetContact.success) {
                    setContact(contactResult.data.acaGetContact.contact);
                    setContactNotes(contactResult.data.acaGetContact.notes);
                    setOpportunities(contactResult.data.acaGetContact.opportunities);
                    setDependents(contactResult.data.acaGetContact.dependents);
                }
            }

        } catch (e: any) {
            console.error(e);
            alert(e.message);
        } finally {
            setIsLoading(false);
        }
    }
    const unmaskData = async () => {
        if (contact) {
            setMasked(false);
            await loadData(contact.contactId, false);
        }
    };
    const maskData = async () => {
        if (contact) {
            setMasked(true);
            await loadData(contact.contactId, true);
        }
    };
    const toggleNotes = async (shouldShowNotes: boolean) => {
        setShowNotes(shouldShowNotes);
        if (!shouldShowNotes) {
            pullHistory();
        }

    };


    const pullHistory = async () => {
        if (contact && contact.contactId) {
            setIsLoading(true);
            const contactId = Number(contact.contactId);
            try {
                const historyResponse = await getHistoryMutation({variables: {contactId: contactId}});
                if (!historyResponse.data.acaGetHistory.success) {
                    alert(historyResponse.data.acaGetHistory.error);
                }
                setHistoryEntries(await prepLinks(historyResponse.data.acaGetHistory.history));
            } catch (e: any) {
                console.log(e);
                alert(e.message);
            } finally {
                setIsLoading(false);
            }
        }
    };
    const popupAddNote = async () => {

        if (contact) {
            presentAddNote({
                onWillDismiss: async (ev: CustomEvent<OverlayEventDetail>) => {
                    setIsLoading(true);
                    if (ev.detail.role === 'confirm') {
                        const popupData = ev.detail.data;
                        let addNoteResponse = await addNoteMutation({variables: {data: {contactId: contact.contactId, noteText: popupData}}});
                        if (!addNoteResponse.data.acaCreateNote.success) {
                            return alert(addNoteResponse.data.acaCreateNote.error);
                        } else {
                            let contactResult = await acaGetContactMutation({variables: {contactId: contact.contactId}});
                            if (contactResult.data?.acaGetContact.success) {
                                setContact(contactResult.data.acaGetContact.contact);
                                setContactNotes(contactResult.data.acaGetContact.notes);
                            }
                        }
                    }
                    setIsLoading(false);
                }
            });
        }
    };
    const prepLinks = async (history: AcaGetHistoryResponseHistory[] | undefined)=> {

        history?.forEach((history) => {
            if (history.changeText.match(/Photo 1 changed from \[(\d+)] to \[(\d+)]/)) {
                const found = history.changeText.match(/Photo 1 changed from \[(\d+)] to \[(\d+)]/);
                if (found && found.length > 2) {
                    history.changeText = 'Photo 1 changed from [<a href="/aca/view-image/' + found[1] + '" target="_blank">' + found[1] + '</a>]'
                        + ' to [<a href="/aca/view-image/'+found[2]+'" target="_blank">'+found[2]+'</a>]';
                }
            } else if (history.changeText.match(/Photo 2 changed from \[(\d+)] to \[(\d+)]/)) {
                const found = history.changeText.match(/Photo 2 changed from \[(\d+)] to \[(\d+)]/);
                if (found && found.length > 2) {
                    history.changeText = 'Photo 2 changed from [<a href="/aca/view-image/' + found[1] + '" target="_blank">' + found[1] + '</a>]'
                        + ' to [<a href="/aca/view-image/'+found[2]+'" target="_blank">'+found[2]+'</a>]';
                }
            } else if (history.changeText.match(/Lead consent PDF changed from \[(\d+)] to \[(\d+)]/) && tier2) {
                const found = history.changeText.match(/Lead consent PDF changed from \[(\d+)] to \[(\d+)]/);
                if (found && found.length > 2) {
                    history.changeText = 'Lead consent PDF changed from [<a href="/aca/view-image/' + found[1] + '" target="_blank">' + found[1] + '</a>]'
                        + ' to [<a href="/aca/view-image/'+found[2]+'" target="_blank">'+found[2]+'</a>]';
                }
            } else if (history.changeText.match(/AOR consent PDF changed from \[(\d+)] to \[(\d+)]/) && tier2) {
                const found = history.changeText.match(/AOR consent PDF changed from \[(\d+)] to \[(\d+)]/);
                if (found && found.length > 2) {
                    history.changeText = 'AOR consent PDF changed from [<a href="/aca/view-image/' + found[1] + '" target="_blank">' + found[1] + '</a>]'
                        + ' to [<a href="/aca/view-image/'+found[2]+'" target="_blank">'+found[2]+'</a>]';
                }
            }
        });
        return history;
    }
    const copyToClipboard = async (contactId: number | null | undefined) => {
        if (contactId) {
            await navigator.clipboard.writeText(contactId.toString());
            setShowPopover(true);
        }
    };

    const promptImageReplace = async (photoNum: number) => {
        if (contact) {
            if (photoNum == 1) {
                setChangingPhoto1(false); //reset the changing option in case it was active
            } else if (photoNum == 2) {
                setChangingPhoto2(false); //reset the changing option in case it was active
            }
            presentImageReplacement({
                onWillDismiss: async (ev: CustomEvent<OverlayEventDetail>) => {
                    if (ev.detail.role === 'confirm') {
                        setIsLoading(true);
                        let contactMethod: 'EMAIL' | 'SMS' = 'EMAIL';
                        if (ev.detail.data === 'SMS') {
                            contactMethod = 'SMS';
                        }

                        let result = await issueImageReplacementMutation({
                            variables: {
                                data: {
                                    type: 'PHOTO' + photoNum,
                                    deliveryMethod: contactMethod,
                                    contactId: contact.contactId,
                                }
                            }
                        });
                        setIsLoading(false);
                        if (!result.data.acaIssueImageReplacement.success) {
                            return alert(result.data.acaIssueImageReplacement.error);
                        }

                        if (contactMethod === 'SMS') {
                            return await presentAlert('Request has been sent to ' + formatPhone(contact.phone));
                        } else {
                            return await presentAlert('Request has been sent to ' + contact.email);
                        }
                    }
                }
            });
        }
    };

    const createNewOpportunity = async () => {
        if (window.confirm('Are you sure you want to create a new opportunity?')) {
            setIsLoading(true);
            const createResponse = await createOpportunityMutation({variables: {data: {contactId: contact?.contactId}}});
            if (createResponse.data.acaCreateOpportunity.success) {
                await loadData(contact?.contactId, true);
                presentAlert('Opportunity created successfully.');
            } else {
                presentAlert('Error Occurred: ' + createResponse.data.acaCreateOpportunity.error);
            }
            setIsLoading(false);
        }
    };

    return (
        <IonPage>

            <AcaHeader pageTitle="ACA Contact Details"></AcaHeader>
            <IonLoading isOpen={isLoading} message={loadingMessage}/>
            <IonContent fullscreen className="aca-contact">
                <div style={{padding: '10px'}}>
                    {contact &&
                        <IonCard>
                            <IonCardHeader>
                                <IonCardTitle>Contact Details&nbsp;
                                    {(tier1 || tier2 || manager) && <IonButton fill="clear" size="small" style={{verticalAlign:'top', marginTop: '0px'}} routerLink={"/aca/edit-contact/" + contact.contactId}><IonIcon md={pencilOutline} ios={pencilSharp}></IonIcon></IonButton>}
                                </IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                {!masked && <div style={{fontWeight: 'bold', padding: '10px', backgroundColor: '#ffdddd', color: '#990000'}}>Warning!!! Data is not masked!</div>}
                                {contact.status === 'NO_CONTACT' && <div style={{fontWeight: 'bold', padding: '10px', backgroundColor: '#ffdddd', color: '#990000'}}>DO NOT CONTACT!!! Customer has requested to not be contacted.</div>}
                                <table style={{float: 'left', borderCollapse: 'separate', borderSpacing: '5px'}}>
                                    <tbody>
                                    <tr>
                                        <th>Contact ID:</th>
                                        <td>{contact.contactId}
                                            {contact && contact.contactId &&
                                                <>&nbsp;&nbsp;
                                                    <a style={{cursor: 'pointer'}} onClick={() => copyToClipboard(contact.contactId)} title="Copy Contact ID">
                                                        <IonIcon id="copyContactBtn" md={copyOutline} ios={copySharp}></IonIcon>
                                                    </a>
                                                    <IonPopover trigger="copyContactBtn" triggerAction="click"
                                                                isOpen={showPopover}
                                                                onDidDismiss={() => setShowPopover(false)}
                                                                onIonPopoverDidPresent={() => setTimeout(setShowPopover, 1000, false)}
                                                    ><IonContent className="ion-padding">Copied to clipboard</IonContent></IonPopover>
                                                </>
                                            }
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>First Name:</th>
                                        <td>{contact.firstName}</td>
                                    </tr>
                                    <tr>
                                        <th>Middle Name:</th>
                                        <td>{contact.middleName}</td>
                                    </tr>
                                    <tr>
                                        <th>Last Name:</th>
                                        <td>{contact.lastName}</td>
                                    </tr>
                                    <tr>
                                        <th>Suffix:</th>
                                        <td>{contact.suffix}</td>
                                    </tr>
                                    <tr>
                                        <th>Email:</th>
                                        <td>{contact.email}</td>
                                    </tr>
                                    <tr>
                                        <th>Phone:</th>
                                        <td>{formatPhone(contact.phone)}</td>
                                    </tr>
                                    <tr>
                                        <th>Address:</th>
                                        <td>
                                            {contact.addressLine1}
                                            {!contact.addressLine1 && <MissingItem field="Address Line 1"></MissingItem>}
                                            <br/>
                                            {contact.addressLine2 &&
                                                <>{contact.addressLine2}<br/></>
                                            }
                                            {contact.addressCity}
                                            {!contact.addressCity && <MissingItem field="Address City"></MissingItem>}
                                            , {contact.addressState} {contact.addressZip}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Date Of Birth:</th>
                                        <td>
                                            <>{contact.dob}</>
                                            {!contact.dob && <MissingItem field="Date of Birth"></MissingItem>}
                                            {masked && (tier2 || manager) && <>&nbsp;&nbsp;<a onClick={() => unmaskData()}><IonIcon md={eyeOutline} ios={eyeSharp}></IonIcon></a></>}
                                            {!masked && (tier2 || manager) && <>&nbsp;&nbsp;<a onClick={() => maskData()}><IonIcon md={eyeOffOutline} ios={eyeOffSharp}></IonIcon></a></>}
                                        </td>
                                    </tr>
                                    <tr>
                                    <th>SSN:</th>
                                        <td>{contact.ssn}
                                            {!contact.ssn && <MissingItem field="Social Security Number"></MissingItem>}
                                            &nbsp;&nbsp;
                                            {masked && (tier2 || manager) && <a onClick={() => unmaskData()}><IonIcon md={eyeOutline} ios={eyeSharp}></IonIcon></a>}
                                            {!masked && (tier2 || manager) && <a onClick={() => maskData()}><IonIcon md={eyeOffOutline} ios={eyeOffSharp}></IonIcon></a>}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>


                                <table style={{
                                    float: 'left',
                                    marginLeft: '40px',
                                    borderCollapse: 'separate',
                                    borderSpacing: '5px'
                                }}>
                                    <tbody>
                                    <tr>
                                        <th>Contact Status:</th>
                                        <td>{formatStatus(contact.status)}</td>
                                    </tr>

                                    <tr>
                                        <th>Fusion Customer ID:</th>
                                        <td>{contact.fusionCustomerId}</td>
                                    </tr>
                                    <tr>
                                        <th>ETC:</th>
                                        <td>{contact.etc}</td>
                                    </tr>
                                    <tr>
                                        <th>Annual Income:</th>
                                        <td>
                                            {contact.annualIncome}
                                            {!contact.annualIncome && contact.annualIncome !== 0 && <MissingItem field="Annual Income"></MissingItem>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Employment Type:</th>
                                        <td>
                                            {contact.employmentType}
                                            {!contact.employmentType && <MissingItem field="Employment Type"></MissingItem>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Employment Description:</th>
                                        <td>{contact.employmentDescription}</td>
                                    </tr>
                                    <tr>
                                        <th>Main Doctor:</th>
                                        <td>{contact.mainDoctor}</td>
                                    </tr>
                                    <tr>
                                        <th>Medications:</th>
                                        <td>{contact.medications}</td>
                                    </tr>
                                    <tr>
                                        <th>Smoker:</th>
                                        <td>{contact.smoker ? 'Yes' : 'No'}</td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2}>
                                            <b>Photo 1:</b>
                                            {contact.photo1Id &&
                                                <>
                                                    <IonButton onClick={() => expandImage(contact.photo1Id)} fill="outline" title="View image">
                                                        <IonIcon md={attachOutline} ios={attachSharp}></IonIcon> Photo 1
                                                    </IonButton>
                                                    {(tier1 || tier2 || manager) &&
                                                        <>
                                                            <IonButton onClick={() => setChangingPhoto1(true)} fill="clear" size="small" title="Manually upload image"><IonIcon md={cloudUploadOutline} ios={cloudUploadSharp}></IonIcon>&nbsp;&nbsp;Change</IonButton>
                                                            <IonButton onClick={() => promptImageReplace(1)} fill="clear" size="small" title="Request new image from contact"><IonIcon md={paperPlaneOutline} ios={paperPlaneSharp}></IonIcon>&nbsp;&nbsp;Request</IonButton>
                                                        </>
                                                    }
                                                </>
                                            }
                                            {!contact.photo1Id && (tier1 || tier2 || manager) &&
                                                <>
                                                    <IonButton onClick={() => setChangingPhoto1(true)} fill="clear" size="small" title="Manually upload image"><IonIcon md={cloudUploadOutline} ios={cloudUploadSharp}></IonIcon>&nbsp;&nbsp;Add
                                                        Photo</IonButton>
                                                    <IonButton onClick={() => promptImageReplace(1)} fill="clear" size="small" title="Request new image from contact"><IonIcon md={paperPlaneOutline} ios={paperPlaneSharp}></IonIcon>&nbsp;&nbsp;Request</IonButton>
                                                </>
                                            }
                                            {changingPhoto1 &&
                                                <>

                                                    <IonItem key="upload"><AcaUploadFile callback={addPhoto1Attachment}/></IonItem>
                                                </>
                                            }

                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2}>
                                            <b>Photo 2:</b>
                                            {contact.photo2Id &&
                                                <><IonButton onClick={() => expandImage(contact.photo2Id)} fill="outline" title="View image">
                                                    <IonIcon md={attachOutline} ios={attachSharp}></IonIcon> Photo 2
                                                </IonButton>
                                                {(tier1 || tier2 || manager) &&
                                                    <>
                                                        <IonButton onClick={() => setChangingPhoto2(true)} fill="clear" size="small" title="Manually upload image"><IonIcon md={cloudUploadOutline} ios={cloudUploadSharp}></IonIcon>&nbsp;&nbsp;Change</IonButton>
                                                        <IonButton onClick={() => promptImageReplace(2)} fill="clear" size="small" title="Request new image from contact"><IonIcon md={paperPlaneOutline} ios={paperPlaneSharp}></IonIcon>&nbsp;&nbsp;Request</IonButton>
                                                    </>
                                                }
                                                </>
                                            }
                                            {!contact.photo2Id && (tier1 || tier2 || manager) &&
                                                <>
                                                    <IonButton onClick={() => setChangingPhoto2(true)} fill="clear" size="small" title="Manually upload image"><IonIcon md={cloudUploadOutline} ios={cloudUploadSharp}></IonIcon>&nbsp;&nbsp;Add Photo</IonButton>
                                                    <IonButton onClick={() => promptImageReplace(2)} fill="clear" size="small" title="Request new image from contact"><IonIcon md={paperPlaneOutline} ios={paperPlaneSharp}></IonIcon>&nbsp;&nbsp;Request</IonButton>
                                                </>
                                            }
                                            {changingPhoto2 &&
                                                <>

                                                    <IonItem key="upload"><AcaUploadFile callback={addPhoto2Attachment} /></IonItem>
                                                </>
                                            }
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>

                                <table style={{
                                    float: 'left',
                                    marginLeft: '40px',
                                    borderCollapse: 'separate',
                                    borderSpacing: '5px'
                                }}>
                                    <tbody style={{color: '#cccccc'}}>
                                    <tr>
                                        <th>Dashboard User ID:</th>
                                        <td>{contact.createdByUserId}</td>
                                    </tr>
                                    <tr>
                                        <th>Source:</th>
                                        <td>{contact.source}</td>
                                    </tr>
                                    <tr>
                                        <th>Dependent Count:</th>
                                        <td>{contact.dependentCount}</td>
                                    </tr>
                                    <tr>
                                        <th>Created Datetime:</th>
                                        <td>{formatDatetime(contact.createdDatetime)}</td>
                                    </tr>

                                    <tr>
                                        <th>Last Updated Datetime:</th>
                                        <td>{formatDatetime(contact.lastUpdatedDatetime)}</td>
                                    </tr>
                                    </tbody>
                                </table>
                                <div style={{clear: 'both'}}></div>
                            </IonCardContent>
                        </IonCard>
                    }

                    {dependents &&
                        <IonCard>
                            <IonCardHeader>
                                <IonCardTitle>Dependents ({dependents.length})</IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>

                                {dependents.map((dependent, index) => {
                                    return (
                                        <div key={'depDiv_'+index} style={{padding: '10px', borderBottom: index!==dependents.length-1 ? '1px solid #cccccc' : '1px solid transparent'}}>
                                            {dependent.firstName} {dependent.middleName} {dependent.lastName} {dependent.suffix}<br />
                                            <b>Date of Birth:</b> {dependent.dob}<br />
                                            <b>SSN:</b> {dependent.ssn}
                                            <div style={{float: 'right', fontSize: '10px', color: '#cccccc'}}>
                                                <b>Created Datetime:</b> {formatDatetime(dependent.createdDatetime)}
                                            </div>
                                        </div>
                                    )
                                })}
                            </IonCardContent>
                        </IonCard>
                    }

                    {opportunities &&
                        <IonCard>
                            <IonCardHeader>
                                <IonCardTitle>Opportunity History</IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <div style={{}}>
                                    {opportunities.filter(opp => opp.opportunityStatus === 'OPEN').length === 0 &&
                                        <IonButton fill="clear" style={{float: 'right'}} size="small" onClick={() => createNewOpportunity()}><IonIcon md={addOutline} ios={addSharp} /> New Opportunity</IonButton>
                                    }
                                    {opportunities.filter(opp => opp.opportunityStatus === 'OPEN').length > 0 &&
                                        <div title="Please make sure all previous opportunities are closed before creating a new opportunity" style={{float: 'right', color: '#999999', padding: '10px'}}><IonIcon md={addOutline} ios={addSharp} /> New Opportunity</div>
                                    }
                                    <table style={{width: '100%'}} className="aca-healthcare">
                                    <thead>
                                        <tr>
                                            <th style={{textAlign: 'center'}}>Opp ID</th>
                                            <th style={{textAlign: 'center'}}>Status</th>
                                            <th style={{textAlign: 'center'}}>Stage</th>
                                            <th style={{textAlign: 'center'}}>Last Updated</th>
                                            <th style={{textAlign: 'center'}}>Created Datetime</th>
                                            <th style={{textAlign: 'center'}}>Assigned User</th>
                                            <th style={{textAlign: 'center'}}>Created By</th>
                                            <th style={{textAlign: 'center'}}>Fusion Agent Id</th>
                                            <th style={{textAlign: 'center'}}>Source</th>
                                            <th style={{textAlign: 'center'}}>Custom Field 1</th>
                                            <th style={{textAlign: 'center'}}>Custom Field 2</th>
                                            <th style={{textAlign: 'center'}}>Success/Fail</th>
                                            <th style={{textAlign: 'center'}}></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {opportunities && opportunities.map((opp, index) => {
                                            return (
                                                <tr key={'opp_' + opp.opportunityId}>
                                                    <td style={{textAlign: 'center'}}>{opp.opportunityId}</td>
                                                    <td style={{textAlign: 'center'}}>{formatStatus(opp.opportunityStatus)}</td>
                                                    <td style={{textAlign: 'center'}}>{opp.stage}</td>
                                                    <td style={{textAlign: 'center'}}>{formatDatetime(opp.lastUpdatedDatetime)}</td>
                                                    <td style={{textAlign: 'center'}}>{formatDatetime(opp.createdDatetime)}</td>
                                                    <td style={{textAlign: 'center'}}>{opp.assignedUserName}</td>
                                                    <td style={{textAlign: 'center'}}>{opp.createdByUserName} {opp.createdByUserIdRef && <>({opp.createdByUserIdRef})</>}</td>
                                                    <td style={{textAlign: 'center'}}>{opp.agentFusionId}</td>
                                                    <td style={{textAlign: 'center'}}>{opp.source}</td>
                                                    <td style={{textAlign: 'center'}}>{opp.customField1}</td>
                                                    <td style={{textAlign: 'center'}}>{opp.customField2}</td>
                                                    <td style={{textAlign: 'left'}}>
                                                        {opp.opportunityStatus === 'CLOSED_LOST' &&
                                                            <div className="danger">
                                                                {opp.failedReasonCode}
                                                            </div>
                                                        }
                                                        {opp.opportunityStatus === 'CLOSED_WON' &&
                                                            <div className="success">
                                                                Prov: {opp.carrierProvider}<br />
                                                                State: {opp.carrierState}<br />
                                                                Mem Count: {opp.carrierMemberCount}
                                                            </div>
                                                        }
                                                    </td>
                                                    <td style={{textAlign: 'center'}} title="Opportunity Details">
                                                    <IonButton size="small" className="xs-button" fill="clear" routerLink={"/aca/view-opportunity/" + opp.opportunityId} routerDirection="none" title="Opportunity Details"><IonIcon md={documentOutline} ios={documentSharp} title="Opportunity Details"></IonIcon></IonButton>
                                                    </td>
                                                </tr>
                                            );
                                        })}


                                        </tbody>
                                    </table>

                                </div>

                            </IonCardContent>
                        </IonCard>


                    }
                    {contact &&
                        <IonCard>
                            <IonCardHeader>
                                <IonCardTitle>Notes</IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <div style={{}}>
                                    {showNotes && <>
                                        <IonButton onClick={() => toggleNotes(true)}>Notes</IonButton>
                                        <IonButton fill="outline" onClick={() => toggleNotes(false)}>History</IonButton>
                                        <IonButton fill="clear" style={{float: "right"}} onClick={() => popupAddNote()}><IonIcon md={addOutline} ios={addSharp}></IonIcon> Add
                                            Note</IonButton>

                                        <div style={{
                                            paddingRight: '20px',
                                            overflow: 'auto',
                                            minWidth: '600px',
                                            height: '400px',
                                            border: '1px solid grey'
                                        }}>
                                            {contactNotes && contactNotes.map((note, index) => {
                                                return (
                                                    <div style={{
                                                        position: 'relative',
                                                        padding: '10px',
                                                        borderTop: '1px solid #cccccc'
                                                    }} key={'note_' + note.noteId}>
                                                        <p>{note.noteText}</p>
                                                        <div className="note" style={{
                                                            display: 'inline-block',
                                                            position: "absolute",
                                                            bottom: '0px',
                                                            right: '0px',
                                                        }}>
                                                            {formatDatetime(note.createdDatetime)}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                            }
                                            {(!contactNotes || contactNotes.length == 0) &&
                                                <div style={{
                                                    position: 'relative',
                                                    color: '#cccccc',
                                                    height: '100%',
                                                    width: '100%',
                                                    textAlign: 'center',
                                                    verticalAlign: 'middle'
                                                }}>
                                                    <div style={{
                                                        display: 'inline-block',
                                                        verticalAlign: 'middle',
                                                        top: '50%',
                                                        position: 'relative'
                                                    }}>
                                                        No Notes
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </>}
                                    {!showNotes && <>
                                        <IonButton fill="outline" onClick={() => toggleNotes(true)}>Notes</IonButton>
                                        <IonButton onClick={() => toggleNotes(false)}>History</IonButton>
                                        <IonButton fill="clear" style={{float: "right"}} onClick={() => popupAddNote()}><IonIcon md={addOutline} ios={addSharp}></IonIcon> Add
                                            Note</IonButton>

                                        <div style={{
                                            paddingRight: '20px',
                                            overflow: 'auto',
                                            minWidth: '600px',
                                            height: '400px',
                                            border: '1px solid grey'
                                        }}>

                                            {historyEntries && historyEntries.map((entry, index) => {
                                                return (
                                                    <div style={{
                                                        position: 'relative',
                                                        padding: '10px',
                                                        borderTop: '1px solid #cccccc'
                                                    }} key={'history_' + index}>
                                                        <div dangerouslySetInnerHTML={{
                                                            __html: entry.changeText
                                                        }}></div>
                                                        <div className="note" style={{
                                                            display: 'inline-block',
                                                            position: "absolute",
                                                            bottom: '0px',
                                                            right: '0px',
                                                        }}>
                                                            {entry.userName} - {formatDatetime(entry.createdDatetime)}
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                            {(!historyEntries || historyEntries.length === 0) &&
                                                <div style={{
                                                    position: 'relative',
                                                    color: '#cccccc',
                                                    height: '100%',
                                                    width: '100%',
                                                    textAlign: 'center',
                                                    verticalAlign: 'middle'
                                                }}>
                                                    <div style={{
                                                        display: 'inline-block',
                                                        verticalAlign: 'middle',
                                                        top: '50%',
                                                        position: 'relative'
                                                    }}>
                                                        No History
                                                    </div>
                                                </div>
                                            }


                                        </div>
                                    </>}
                                </div>
                            </IonCardContent>
                        </IonCard>
                    }
                </div>
            </IonContent>
        </IonPage>
    )
}

export default AcaViewContact;